import React, { useState } from "react";
import LayersIcon from '@mui/icons-material/Layers';
import ScienceIcon from '@mui/icons-material/Science';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
];

const ANIOS = Array.from({ length: 6 }, (_, i) => (2024 + i).toString());

const CondicionesSection = ({
  condicionesRecords = [],
  isFormOpen,
  setIsFormOpen,
  formData = {},
  setFormData,
  handleSaveCondiciones
}) => {
  const [selectedRecord, setSelectedRecord] = useState(null);

  const mesActual = MESES[new Date().getMonth()];
  const anioActual = new Date().getFullYear().toString();

  const selectedMes = formData?.mes || mesActual;
  const selectedAnio = formData?.anio || anioActual;
  const lecturas = formData?.lecturas || [
    { id: Date.now(), dia: 1, hora: "08:00", temp: "", hum: "" },
    { id: Date.now() + 1, dia: 1, hora: "14:00", temp: "", hum: "" }
  ];
  const observaciones = formData?.observaciones || "";

  const setField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Agregar una nueva lectura para un día
  const handleAddLectura = (diaTarget = 1) => {
    const newLectura = {
      id: Date.now() + Math.random(),
      dia: Number(diaTarget),
      hora: "12:00",
      temp: "",
      hum: ""
    };
    setFormData(prev => ({
      ...prev,
      lecturas: [...(prev.lecturas || lecturas), newLectura]
    }));
  };

  // Eliminar una lectura específica
  const handleRemoveLectura = (id) => {
    setFormData(prev => ({
      ...prev,
      lecturas: (prev.lecturas || lecturas).filter(item => item.id !== id)
    }));
  };

  // Actualizar una lectura específica
  const handleLecturaChange = (id, field, value) => {
    setFormData(prev => {
      const updated = (prev.lecturas || lecturas).map(item => {
        if (item.id === id) {
          return { ...item, [field]: field === 'dia' ? Number(value) : value };
        }
        return item;
      });
      return { ...prev, lecturas: updated };
    });
  };

  const handleEditRecord = (rec) => {
    if (rec.status !== "borrador" && rec.status !== "rechazado") {
      alert("Solo se pueden editar registros que se encuentren en estado BORRADOR o RECHAZADO.");
      return;
    }
    setFormData({
      mes: rec.mes || rec.mesAnio?.split(" ")[0] || mesActual,
      anio: rec.anio || rec.mesAnio?.split(" ")[1] || anioActual,
      lecturas: rec.lecturas && rec.lecturas.length > 0 ? rec.lecturas : rec.dailyData || [],
      observaciones: rec.observaciones || ""
    });
    setIsFormOpen(true);
  };

  // Cálculo de promedios para el resumen
  const validLecturas = lecturas.filter(l => l.temp !== "" || l.hum !== "");
  const avgTemp = validLecturas.length > 0 
    ? (validLecturas.reduce((acc, curr) => acc + (parseFloat(curr.temp) || 0), 0) / validLecturas.length).toFixed(1)
    : "-";
  const avgHum = validLecturas.length > 0 
    ? (validLecturas.reduce((acc, curr) => acc + (parseFloat(curr.hum) || 0), 0) / validLecturas.length).toFixed(1)
    : "-";

  // VISTA: DETALLE COMPLETO DE UN REGISTRO HISTÓRICO
  if (selectedRecord) {
    const recLecturas = selectedRecord.lecturas || selectedRecord.registros || [];
    const validRecs = recLecturas.filter(l => l.temp || l.hum);
    const recAvgTemp = validRecs.length > 0 ? (validRecs.reduce((a, b) => a + (parseFloat(b.temp) || 0), 0) / validRecs.length).toFixed(1) : "-";
    const recAvgHum = validRecs.length > 0 ? (validRecs.reduce((a, b) => a + (parseFloat(b.hum) || 0), 0) / validRecs.length).toFixed(1) : "-";

    return (
      <div className="animate-fadeIn">
        <div className="history-section-header" style={{ marginBottom: "20px" }}>
          <button 
            onClick={() => setSelectedRecord(null)} 
            className="btn-secondary-custom" 
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 16px" }}
          >
            <ArrowBackIcon style={{ fontSize: 16 }} />
            <span>Volver al Historial</span>
          </button>
          <h2 style={{ fontSize: "20px", fontWeight: 800, color: "var(--agave-green)" }}>
            Monitoreo Ambiental: {selectedRecord.mes} {selectedRecord.anio || selectedRecord.mesAnio}
          </h2>
        </div>

        <div className="detail-card detail-card-accent-left-green" style={{ marginBottom: "24px", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "14px", padding: "20px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "16px" }}>
            <div>
              <small style={{ color: "var(--text-muted)", fontSize: "11px", textTransform: "uppercase", fontWeight: 700 }}>Mes / Año Monitoreado</small>
              <p style={{ margin: "4px 0 0 0", fontSize: "16px", fontWeight: 800, color: "var(--agave-green)" }}>
                {selectedRecord.mes || selectedRecord.mesAnio} {selectedRecord.anio || ''}
              </p>
            </div>
            <div>
              <small style={{ color: "var(--text-muted)", fontSize: "11px", textTransform: "uppercase", fontWeight: 700 }}>Total Lecturas Registradas</small>
              <p style={{ margin: "4px 0 0 0", fontSize: "15px", fontWeight: 800 }}>{recLecturas.length} lecturas</p>
            </div>
            <div>
              <small style={{ color: "var(--text-muted)", fontSize: "11px", textTransform: "uppercase", fontWeight: 700 }}>Temp. Promedio Mensual</small>
              <p style={{ margin: "4px 0 0 0", fontSize: "15px", fontWeight: 800, color: "var(--copper)" }}>{recAvgTemp} {recAvgTemp !== "-" ? "°C" : ""}</p>
            </div>
            <div>
              <small style={{ color: "var(--text-muted)", fontSize: "11px", textTransform: "uppercase", fontWeight: 700 }}>Humedad Promedio Mensual</small>
              <p style={{ margin: "4px 0 0 0", fontSize: "15px", fontWeight: 800, color: "#3498db" }}>{recAvgHum} {recAvgHum !== "-" ? "%" : ""}</p>
            </div>
            <div>
              <small style={{ color: "var(--text-muted)", fontSize: "11px", textTransform: "uppercase", fontWeight: 700 }}>Revisor (V.Bo. Inspector)</small>
              <p style={{ margin: "4px 0 0 0", fontSize: "14px", fontWeight: 600 }}>{selectedRecord.inspector || "Pendiente V.Bo."}</p>
            </div>
            <div>
              <small style={{ color: "var(--text-muted)", fontSize: "11px", textTransform: "uppercase", fontWeight: 700 }}>Estado</small>
              <p style={{ margin: "4px 0 0 0" }}>
                <span className={`badge-status ${selectedRecord.status}`}>
                  {selectedRecord.status === "aprobado" ? "Aprobado" : selectedRecord.status === "revision" ? "En revisión" : "Borrador"}
                </span>
              </p>
            </div>
          </div>
        </div>

        <div className="records-list-wrapper" style={{ borderRadius: "14px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.08)" }}>
          <table className="records-table">
            <thead>
              <tr>
                <th># Lectura</th>
                <th>Día del Mes</th>
                <th>Hora de Registro</th>
                <th>Temperatura Ambientación (°C)</th>
                <th>% Humedad Relativa</th>
              </tr>
            </thead>
            <tbody>
              {recLecturas.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center", padding: "30px", color: "var(--text-muted)" }}>
                    No hay lecturas registradas para este periodo.
                  </td>
                </tr>
              ) : (
                recLecturas.map((lec, idx) => (
                  <tr key={idx}>
                    <td style={{ fontWeight: 700, color: "var(--text-muted)" }}>#{idx + 1}</td>
                    <td style={{ fontWeight: 800, color: "var(--agave-green)" }}>Día {lec.dia}</td>
                    <td>
                      <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <AccessTimeIcon style={{ fontSize: 15, opacity: 0.7 }} />
                        {lec.hora || "-"}
                      </span>
                    </td>
                    <td style={{ color: "var(--copper)", fontWeight: 700 }}>{lec.temp ? `${lec.temp} °C` : "-"}</td>
                    <td style={{ color: "#3498db", fontWeight: 700 }}>{lec.hum ? `${lec.hum} %` : "-"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // VISTA 1: TABLA PRINCIPAL DE HISTORIAL DE MESES
  if (!isFormOpen) {
    return (
      <div className="animate-fadeIn">
        <div className="history-section-header" style={{ marginBottom: "20px" }}>
          <div>
            <h2 style={{ fontSize: "20px", fontWeight: 800, color: "var(--agave-green)" }}>
              Registro de Condiciones Ambientales Mensuales (Sección VIII)
            </h2>
            <p style={{ margin: "4px 0 0 0", color: "var(--text-muted)", fontSize: "13px" }}>
              Monitoreo continuo de Temperatura (°C) y Humedad Relativa (%) en bodega y área de maduración.
            </p>
          </div>

          <button 
            onClick={() => {
              setFormData({
                mes: mesActual,
                anio: anioActual,
                lecturas: [
                  { id: Date.now(), dia: 1, hora: "08:00", temp: "", hum: "" },
                  { id: Date.now() + 1, dia: 1, hora: "14:00", temp: "", hum: "" },
                  { id: Date.now() + 2, dia: 2, hora: "08:00", temp: "", hum: "" }
                ],
                observaciones: ""
              });
              setIsFormOpen(true);
            }}
            className="btn-add-new"
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 18px" }}
          >
            <AddIcon style={{ fontSize: 20 }} />
            <span style={{ fontWeight: 700 }}>Registrar Nuevo Mes</span>
          </button>
        </div>

        <div className="records-list-wrapper" style={{ borderRadius: "14px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.08)" }}>
          <table className="records-table">
            <thead>
              <tr>
                <th>Mes / Año</th>
                <th>Lecturas Tomadas</th>
                <th>Temp. Promedio (°C)</th>
                <th>Humedad Promedio (%)</th>
                <th>Observaciones</th>
                <th>Revisor (V.Bo. Inspector)</th>
                <th>Estado</th>
                <th style={{ textAlign: "center" }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {condicionesRecords.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: "center", padding: "40px", color: "var(--text-muted)" }}>
                    <DeviceThermostatIcon style={{ fontSize: 40, opacity: 0.4, marginBottom: "8px" }} />
                    <p style={{ margin: 0, fontSize: "14px" }}>No hay registros de condiciones ambientales para ningún mes.</p>
                  </td>
                </tr>
              ) : (
                condicionesRecords.map((rec, i) => {
                  const items = rec.lecturas || rec.registros || [];
                  const validItems = items.filter(d => d.temp || d.hum);
                  const avgTempHist = validItems.length ? (validItems.reduce((acc, curr) => acc + parseFloat(curr.temp || 0), 0) / validItems.length).toFixed(1) : "-";
                  const avgHumHist = validItems.length ? (validItems.reduce((acc, curr) => acc + parseFloat(curr.hum || 0), 0) / validItems.length).toFixed(1) : "-";

                  return (
                    <tr key={i}>
                      <td style={{ fontWeight: 800, color: "var(--agave-green)", fontSize: "15px" }}>
                        {rec.mes || rec.mesAnio} {rec.anio || ''}
                      </td>
                      <td>
                        <strong>{items.length}</strong> lecturas en el mes
                      </td>
                      <td style={{ color: "var(--copper)", fontWeight: 700 }}>
                        {avgTempHist} {avgTempHist !== "-" ? "°C" : ""}
                      </td>
                      <td style={{ color: "#3498db", fontWeight: 700 }}>
                        {avgHumHist} {avgHumHist !== "-" ? "%" : ""}
                      </td>
                      <td style={{ maxWidth: "220px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {rec.observaciones || "Sin observaciones"}
                      </td>
                      <td style={{ fontSize: "12px", fontWeight: 600 }}>{rec.inspector || "Pendiente V.Bo."}</td>
                      <td>
                        <span className={`badge-status ${rec.status}`}>
                          {rec.status === "aprobado" ? "Aprobado" : rec.status === "revision" ? "En revisión" : "Borrador"}
                        </span>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <div style={{ display: "flex", justifyContent: "center", gap: "6px" }}>
                          <button 
                            type="button" 
                            onClick={() => setSelectedRecord(rec)}
                            title="Ver Detalle"
                            style={{ background: "rgba(52, 152, 219, 0.15)", color: "#3498db", border: "1px solid rgba(52, 152, 219, 0.3)", borderRadius: "6px", padding: "5px 8px", cursor: "pointer", display: "flex", alignItems: "center" }}
                          >
                            <VisibilityIcon style={{ fontSize: 16 }} />
                          </button>

                          <button 
                            type="button" 
                            onClick={() => handleEditRecord(rec)}
                            disabled={rec.status !== "borrador" && rec.status !== "rechazado"}
                            title={rec.status === "borrador" || rec.status === "rechazado" ? "Editar Registro" : "Solo editables en estado Borrador"}
                            style={{ 
                              background: rec.status === "borrador" || rec.status === "rechazado" ? "rgba(241, 196, 15, 0.15)" : "rgba(255,255,255,0.04)", 
                              color: rec.status === "borrador" || rec.status === "rechazado" ? "#f1c40f" : "var(--text-muted)", 
                              border: rec.status === "borrador" || rec.status === "rechazado" ? "1px solid rgba(241, 196, 15, 0.3)" : "1px solid rgba(255,255,255,0.08)", 
                              borderRadius: "6px", 
                              padding: "5px 8px", 
                              cursor: rec.status === "borrador" || rec.status === "rechazado" ? "pointer" : "not-allowed", 
                              display: "flex", 
                              alignItems: "center"
                            }}
                          >
                            <EditIcon style={{ fontSize: 16 }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // VISTA 2: FORMULARIO DE CAPTURA DEL MES (SELECCIÓN MES Y AÑO + MULTIPLES LECTURAS POR DÍA)
  return (
    <div className="animate-fadeIn">
      <div className="history-section-header" style={{ marginBottom: "20px" }}>
        <button 
          onClick={() => setIsFormOpen(false)} 
          className="btn-secondary-custom" 
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 16px" }}
        >
          <ArrowBackIcon style={{ fontSize: 16 }} />
          <span>Volver al Historial</span>
        </button>
        <h2 style={{ fontSize: "20px", fontWeight: 800, color: "var(--agave-green)" }}>
          Captura de Condiciones Ambientales Mensuales - Sección VIII
        </h2>
      </div>

      <div className="cards-container-grid">
        {/* PASO 1: SELECCION DE MES Y AÑO */}
        <section className="detail-card detail-card-accent-left-green" style={{ gridColumn: "1 / -1", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "16px", padding: "24px" }}>
          <header className="detail-card-header" style={{ marginBottom: "20px" }}>
            <div className="detail-card-icon-wrapper-green" style={{ width: "42px", height: "42px", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(46, 204, 113, 0.15)", color: "#2ecc71" }}>
              <CalendarMonthIcon style={{ fontSize: 24 }} />
            </div>
            <div className="detail-card-title-wrapper">
              <h2 style={{ fontSize: "18px", fontWeight: 800, margin: 0 }}>Paso 1: Selección del Periodo (Mes y Año)</h2>
              <p style={{ margin: "2px 0 0 0", color: "var(--text-muted)", fontSize: "13px" }}>
                Elija el mes y año correspondiente al reporte de condiciones ambientales.
              </p>
            </div>
          </header>

          <div className="species-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <div className="custom-input-group">
              <select 
                value={selectedMes} 
                onChange={e => setField("mes", e.target.value)}
                className="custom-select-field"
                style={{ padding: "18px 16px 8px 16px", fontWeight: 700, fontSize: "15px" }}
              >
                {MESES.map((m, idx) => (
                  <option key={idx} value={m}>{m}</option>
                ))}
              </select>
              <label className="custom-input-label">Mes de Monitoreo</label>
            </div>

            <div className="custom-input-group">
              <select 
                value={selectedAnio} 
                onChange={e => setField("anio", e.target.value)}
                className="custom-select-field"
                style={{ padding: "18px 16px 8px 16px", fontWeight: 700, fontSize: "15px" }}
              >
                {ANIOS.map((a, idx) => (
                  <option key={idx} value={a}>{a}</option>
                ))}
              </select>
              <label className="custom-input-label">Año</label>
            </div>
          </div>

          {/* TARJETA DE RESUMEN EN TIEMPO REAL */}
          <div style={{ marginTop: "16px", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.08)", padding: "16px", borderRadius: "12px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px" }}>
            <div>
              <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", display: "block" }}>Periodo Seleccionado:</span>
              <strong style={{ color: "var(--agave-green)", fontSize: "16px" }}>{selectedMes} {selectedAnio}</strong>
            </div>
            <div>
              <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", display: "block" }}>Lecturas Registradas:</span>
              <strong style={{ fontSize: "16px", color: "white" }}>{lecturas.length} entradas</strong>
            </div>
            <div>
              <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", display: "block" }}>Promedio Temperatura:</span>
              <strong style={{ color: "var(--copper)", fontSize: "16px" }}>{avgTemp} {avgTemp !== "-" ? "°C" : ""}</strong>
            </div>
            <div>
              <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", display: "block" }}>Promedio Humedad:</span>
              <strong style={{ color: "#3498db", fontSize: "16px" }}>{avgHum} {avgHum !== "-" ? "%" : ""}</strong>
            </div>
          </div>
        </section>

        {/* PASO 2: LECTURAS MULTIPLES POR DIA */}
        <section className="detail-card detail-card-accent-left-copper" style={{ gridColumn: "1 / -1", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "16px", padding: "24px" }}>
          <header className="detail-card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
              <div className="detail-card-icon-wrapper-copper" style={{ width: "42px", height: "42px", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(210, 105, 30, 0.15)", color: "var(--copper)" }}>
                <DeviceThermostatIcon style={{ fontSize: 24 }} />
              </div>
              <div className="detail-card-title-wrapper">
                <h2 style={{ fontSize: "18px", fontWeight: 800, margin: 0 }}>Paso 2: Registro de Lecturas (Múltiples Tomas por Día)</h2>
                <p style={{ margin: "2px 0 0 0", color: "var(--text-muted)", fontSize: "13px" }}>
                  Puede ingresar múltiples tomas de temperatura y humedad en un mismo día (ej. Mañana, Tarde, Noche).
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => handleAddLectura(1)}
              className="btn-add-new"
              style={{ background: "rgba(46, 204, 113, 0.15)", color: "#2ecc71", border: "1px solid rgba(46, 204, 113, 0.4)", padding: "8px 16px", borderRadius: "10px" }}
            >
              <AddIcon style={{ fontSize: 18 }} />
              <span style={{ fontWeight: 700 }}>+ Agregar Toma / Lectura</span>
            </button>
          </header>

          <div style={{ overflowX: "auto" }}>
            <table className="records-table" style={{ background: "rgba(0,0,0,0.2)", borderRadius: "12px" }}>
              <thead>
                <tr>
                  <th style={{ width: "60px" }}>#</th>
                  <th style={{ width: "140px" }}>Día del Mes (1-31)</th>
                  <th style={{ width: "160px" }}>Hora de Registro</th>
                  <th>Temperatura (°C)</th>
                  <th>Humedad Relativa (%)</th>
                  <th style={{ textAlign: "center", width: "80px" }}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {lecturas.map((item, index) => (
                  <tr key={item.id || index}>
                    <td style={{ fontWeight: 700, color: "var(--text-muted)" }}>{index + 1}</td>
                    <td>
                      <div className="custom-input-group" style={{ margin: 0 }}>
                        <select
                          value={item.dia}
                          onChange={e => handleLecturaChange(item.id, "dia", e.target.value)}
                          className="custom-select-field"
                          style={{ padding: "10px 12px", height: "42px", fontWeight: 700 }}
                        >
                          {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                            <option key={d} value={d}>Día {d}</option>
                          ))}
                        </select>
                      </div>
                    </td>
                    <td>
                      <div className="custom-input-group" style={{ margin: 0 }}>
                        <input
                          type="time"
                          value={item.hora || "12:00"}
                          onChange={e => handleLecturaChange(item.id, "hora", e.target.value)}
                          className="custom-input-field"
                          style={{ padding: "8px 12px", height: "42px", fontWeight: 600 }}
                        />
                      </div>
                    </td>
                    <td>
                      <div className="custom-input-group" style={{ margin: 0 }}>
                        <input
                          type="number"
                          step="0.1"
                          placeholder="Ej. 24.5"
                          value={item.temp || ""}
                          onChange={e => handleLecturaChange(item.id, "temp", e.target.value)}
                          className="custom-input-field"
                          style={{ padding: "8px 12px", height: "42px" }}
                        />
                        <span className="input-suffix" style={{ color: "var(--copper)", right: "12px", top: "12px" }}>°C</span>
                      </div>
                    </td>
                    <td>
                      <div className="custom-input-group" style={{ margin: 0 }}>
                        <input
                          type="number"
                          step="0.1"
                          placeholder="Ej. 55"
                          value={item.hum || ""}
                          onChange={e => handleLecturaChange(item.id, "hum", e.target.value)}
                          className="custom-input-field"
                          style={{ padding: "8px 12px", height: "42px" }}
                        />
                        <span className="input-suffix" style={{ color: "#3498db", right: "12px", top: "12px" }}>%</span>
                      </div>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {lecturas.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveLectura(item.id)}
                          style={{ background: "rgba(231, 76, 60, 0.15)", color: "#e74c3c", border: "1px solid rgba(231, 76, 60, 0.3)", borderRadius: "8px", padding: "8px", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                          title="Eliminar esta toma"
                        >
                          <DeleteIcon style={{ fontSize: 18 }} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: "16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "12.5px", color: "var(--text-muted)" }}>
              Tip: Puede añadir cuantas tomas necesite por día.
            </span>
            <button
              type="button"
              onClick={() => handleAddLectura(lecturas.length ? lecturas[lecturas.length - 1].dia : 1)}
              className="btn-secondary-custom"
              style={{ padding: "6px 14px", fontSize: "13px", display: "flex", alignItems: "center", gap: 6 }}
            >
              <AddIcon style={{ fontSize: 16 }} />
              <span>Añadir otra toma</span>
            </button>
          </div>
        </section>

        {/* PASO 3: OBSERVACIONES */}
        <section className="detail-card detail-card-accent-left-green" style={{ gridColumn: "1 / -1", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "16px", padding: "24px" }}>
          <header className="detail-card-header" style={{ marginBottom: "16px" }}>
            <div className="detail-card-icon-wrapper-green" style={{ width: "42px", height: "42px", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(46, 204, 113, 0.15)", color: "#2ecc71" }}>
              <SettingsIcon style={{ fontSize: 24 }} />
            </div>
            <div className="detail-card-title-wrapper">
              <h2 style={{ fontSize: "18px", fontWeight: 800, margin: 0 }}>Paso 3: Observaciones y Notas Generales</h2>
              <p style={{ margin: "2px 0 0 0", color: "var(--text-muted)", fontSize: "13px" }}>
                Describa novedades climáticas excepcionales u observaciones del equipo de medición.
              </p>
            </div>
          </header>

          <div className="custom-input-group">
            <textarea 
              id="condObservaciones" 
              value={observaciones} 
              onChange={e => setField("observaciones", e.target.value)} 
              className="custom-input-field"
              style={{ height: "90px", resize: "none", padding: "14px" }}
              placeholder="Escriba aquí observaciones climáticas del mes..."
            />
            <label className="custom-input-label" htmlFor="condObservaciones">Observaciones Generales del Mes</label>
          </div>
        </section>
      </div>

      <div className="button-row" style={{ marginTop: "24px", display: "flex", justifyContent: "flex-end", gap: "14px" }}>
        <button type="button" className="btn-secondary-custom" onClick={() => setIsFormOpen(false)} style={{ padding: "12px 24px", borderRadius: "10px" }}>
          Volver
        </button>
        <button 
          type="button" 
          className="btn-primary-custom" 
          onClick={() => handleSaveCondiciones("revision")}
          style={{ padding: "12px 28px", borderRadius: "10px", display: "flex", alignItems: "center", gap: 8 }}
        >
          <CheckCircleIcon style={{ fontSize: 20 }} />
          <span style={{ fontWeight: 800 }}>Registrar Monitoreo Sección VIII</span>
        </button>
      </div>
    </div>
  );
};

export default CondicionesSection;

